import { App, Image, Spin } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MobileEmpty } from '@/components/mobile/MobileEmpty';
import { ReportProjectWorkItemsDisplay } from '@/components/report/ReportProjectWorkItemsDisplay';
import { REPORT_STATUS_LABELS } from '@/constants/report';
import { ROUTES } from '@/constants/routes';
import { useTeamReportDetail } from '@/hooks/useTeamReportDetail';
import * as departmentsApi from '@/services/api/departments';
import * as usersApi from '@/services/api/users';
import { flattenDepartmentTree } from '@/utils/department-tree';

export function TeamReportDetailMobilePage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const { loading, report, comments, imageUrls, canComment, createComment } =
    useTeamReportDetail(reportId);

  const [departmentName, setDepartmentName] = useState('—');
  const [jobTitle, setJobTitle] = useState('—');
  const [composing, setComposing] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadMeta = useCallback(async () => {
    if (!report) {
      return;
    }

    try {
      const [tree, submitter] = await Promise.all([
        departmentsApi.fetchDepartmentTree(),
        usersApi.fetchUser(report.userId),
      ]);
      const map = new Map<string, string>();
      for (const dept of flattenDepartmentTree(tree)) {
        map.set(dept.id, dept.name);
      }
      setDepartmentName(map.get(report.user.departmentId) ?? '—');
      setJobTitle(submitter.jobTitle?.trim() || '—');
    } catch {
      setDepartmentName('—');
      setJobTitle('—');
    }
  }, [report]);

  useEffect(() => {
    void loadMeta();
  }, [loadMeta]);

  const goBackToTeam = () => {
    navigate(ROUTES.mobile.leader.team, { replace: true });
  };

  const handleSubmitComment = async () => {
    const content = commentText.trim();
    if (!content) {
      message.warning('请填写点评内容');
      return;
    }

    setSubmitting(true);
    try {
      await createComment(content);
      setCommentText('');
      setComposing(false);
      goBackToTeam();
    } catch (error) {
      if (error instanceof Error && error.message) {
        message.error(error.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!reportId) {
    return <MobileEmpty message="无效的日报" />;
  }

  if (!loading && !report) {
    return <MobileEmpty message="日报不存在或无权查看" />;
  }

  return (
    <div className="mobile-detail-page">
      <div className="mobile-detail-page__body">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin />
          </div>
        ) : report ? (
          <>
            <section className="mobile-section">
              <div className="mobile-detail-row">
                <span className="mobile-detail-label">提交人</span>
                <span className="mobile-detail-value">{report.user.name}</span>
              </div>
              <div className="mobile-detail-row">
                <span className="mobile-detail-label">职务</span>
                <span className="mobile-detail-value">{jobTitle}</span>
              </div>
              <div className="mobile-detail-row">
                <span className="mobile-detail-label">部门</span>
                <span className="mobile-detail-value">{departmentName}</span>
              </div>
              <div className="mobile-detail-row">
                <span className="mobile-detail-label">日期</span>
                <span className="mobile-detail-value">
                  {new Date(report.reportDate).toLocaleDateString('zh-CN')}
                </span>
              </div>
              <div className="mobile-detail-row">
                <span className="mobile-detail-label">状态</span>
                <span className="mobile-detail-value">{REPORT_STATUS_LABELS[report.status]}</span>
              </div>
            </section>

            <section className="mobile-section">
              <div className="mobile-section__title" style={{ marginBottom: 8 }}>
                今日工作
              </div>
              <p className="mobile-detail-text">{report.todayWork}</p>
            </section>

            <section className="mobile-section">
              <div className="mobile-section__title" style={{ marginBottom: 8 }}>
                明日计划
              </div>
              <p className="mobile-detail-text">{report.tomorrowPlan?.trim() || '—'}</p>
            </section>

            <section className="mobile-section">
              <div className="mobile-section__title" style={{ marginBottom: 8 }}>
                问题与风险
              </div>
              <p className="mobile-detail-text">{report.problemRemark?.trim() || '—'}</p>
            </section>

            <ReportProjectWorkItemsDisplay report={report} isMobile />

            {report.images.length > 0 ? (
              <section className="mobile-section">
                <div className="mobile-section__title" style={{ marginBottom: 8 }}>
                  图片（{report.images.length}）
                </div>
                <Image.PreviewGroup>
                  <div className="mobile-detail-images">
                    {report.images.map((image) => (
                      <Image
                        key={image.id}
                        width={100}
                        height={100}
                        style={{ objectFit: 'cover', borderRadius: 6 }}
                        src={imageUrls[image.id] || undefined}
                        alt={image.originalName}
                      />
                    ))}
                  </div>
                </Image.PreviewGroup>
              </section>
            ) : null}

            <section className="mobile-section">
              <div className="mobile-section__header">
                <span className="mobile-section__title">点评记录</span>
                <span className="mobile-section__meta">{comments.length} 条</span>
              </div>
              {comments.length === 0 ? (
                <p className="mobile-hint" style={{ margin: 0, textAlign: 'center' }}>
                  暂无点评
                </p>
              ) : (
                comments.map((item) => (
                  <div key={item.id} className="mobile-comment-item">
                    <div className="mobile-comment-item__meta">
                      {new Date(item.createdAt).toLocaleString('zh-CN')}
                      <span className="mobile-tag" style={{ marginLeft: 8 }}>
                        {item.readStatus === 'READ' ? '员工已读' : '员工未读'}
                      </span>
                    </div>
                    <p className="mobile-comment-item__content">{item.content}</p>
                  </div>
                ))
              )}
            </section>

            {composing ? (
              <section className="mobile-section mobile-comment-compose">
                <div className="mobile-section__title" style={{ marginBottom: 8 }}>
                  填写点评
                </div>
                <textarea
                  className="mobile-comment-compose__input"
                  rows={5}
                  placeholder="请输入点评内容"
                  value={commentText}
                  onChange={(event) => setCommentText(event.target.value)}
                  maxLength={5000}
                />
                <div className="mobile-comment-compose__actions">
                  <button
                    type="button"
                    className="mobile-btn mobile-btn--ghost"
                    onClick={() => {
                      setComposing(false);
                      setCommentText('');
                    }}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    className="mobile-btn mobile-btn--primary"
                    disabled={submitting}
                    onClick={() => void handleSubmitComment()}
                  >
                    {submitting ? '提交中…' : '提交点评'}
                  </button>
                </div>
              </section>
            ) : null}
          </>
        ) : null}
      </div>

      {report && canComment && !composing ? (
        <footer className="mobile-detail-footer">
          <button
            type="button"
            className="mobile-btn mobile-btn--primary mobile-btn--block"
            onClick={() => setComposing(true)}
          >
            点评
          </button>
        </footer>
      ) : null}
    </div>
  );
}
